import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ContactService } from '../../../contact.service';
import { debounceTime, startWith, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TagsGridDialogComponent } from '../../../../tags/tags-list-dialog.component';
import { ContactSyncModel, TagModel } from '../../../../api/models';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { NgIf, NgFor } from '@angular/common';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelContent } from '@angular/material/expansion';
import { FlexModule } from '@angular/flex-layout';
import { MatTagsMultiselectComponent } from '../../../../editors/mat-tags-multiselect/mat-tags-multiselect.component';
import { MatTextInputComponent } from '../../../../editors/mat-text-input/mat-text-input.component';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';

@Component({
    selector: 'app-integration-custom-field-settings',
    templateUrl: './integration-custom-field-settings.component.html',
    styleUrls: ['./integration-custom-field-settings.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        FlexModule,
        MatCheckbox,
        FormsModule,
        MatTagsMultiselectComponent,
        MatExpansionPanelContent,
        MatTextInputComponent,
        ReactiveFormsModule,
        MatTable,
        NgFor,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
    ],
})
export class IntegrationCustomFieldSettingsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() sectionGroup: FormGroup;
  @Input() integration: ContactSyncModel;
  @Input() canEditTags: boolean;
  @Input() submitted: boolean;

  public readonly hhaSyncProvider = 'hhaExchange';
  public readonly searchControl = new UntypedFormControl();
  public readonly tableColumns = [
    { key: 'fieldTitle', label: 'Field Title' },
    { key: 'enableField', label: 'Enable Field' },
    { key: 'includeInSearch', label: 'Include in Search' },
  ];
  public readonly tableColumnKeys = this.tableColumns.map(col => col.key);

  private _filteredSectionFieldGroups: UntypedFormGroup[] = [];
  private _currentPage = 0;
  private _pageSize = 5;

  private readonly _subscriptions = new Subscription();

  constructor(
    private readonly _contactService: ContactService,
    private readonly _dialog: MatDialog,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  get paginatedFields(): UntypedFormGroup[] {
    const startIndex = this._currentPage * this._pageSize;
    return this._filteredSectionFieldGroups.slice(startIndex, startIndex + this._pageSize);
  }

  get allTags(): TagModel[] {
    return this._contactService.allTags;
  }

  get filteredSectionFieldGroups(): UntypedFormGroup[] {
    return this._filteredSectionFieldGroups;
  }

  get fieldGroups(): UntypedFormGroup[] {
    const fields = this.sectionGroup?.get('fields') as UntypedFormArray;

    return [...(fields.controls || [])] as UntypedFormGroup[];
  }

  public ngOnInit(): void {
    this._subscriptions.add(this.subscribeCustomFieldSearch());
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public onPageChange(event: PageEvent): void {
    const scrollPosition = window.scrollY;

    console.log(scrollPosition);
    this._currentPage = event.pageIndex;
    this._pageSize = event.pageSize;

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }

  onCheckboxCheck({ checked }: MatCheckboxChange, checkboxType: string, fieldGroup: UntypedFormGroup) {
    if (checkboxType !== 'enableField') {
      return;
    }

    const includeInSearchControl = fieldGroup.get('includeInSearch');

    if (checked) {
      includeInSearchControl.enable();
      return;
    }

    includeInSearchControl.patchValue(false);
    includeInSearchControl.disable();
  }

  public trackBy(index: number, formGroup: any): string {
    const { fieldKey, title } = formGroup.getRawValue() || {};

    return fieldKey || title + index;
  }

  public onManageTagsClick($event: MouseEvent): void {
    $event.stopPropagation();
    const dialogRef = this._dialog.open(TagsGridDialogComponent, {
      width: '600px',
      height: '90%',
    });

    dialogRef.afterClosed().subscribe(async () => {
      await this._contactService.getTags();
    });
  }

  public isHhaSection(sectionGroup: FormGroup, title: string): boolean {
    return sectionGroup.get('title')?.value === title;
  }

  private filterFieldsForSection(searchText: string): void {
    if (!searchText.trim()) {
      this._filteredSectionFieldGroups = this.fieldGroups;
      return;
    }

    const filtered = this.fieldGroups.filter(field =>
      field.get('fieldTitle').value.toLowerCase().includes(searchText.toLowerCase()),
    );

    this._filteredSectionFieldGroups = filtered;
  }

  private subscribeCustomFieldSearch(): Subscription {
    return this.searchControl.valueChanges.pipe(startWith(''), debounceTime(300)).subscribe(searchText => {
      this.filterFieldsForSection(searchText || '');
      this.paginator?.firstPage();
      this._cdr.detectChanges();
    });
  }
}
